"use client";

import { forwardRef, type KeyboardEvent } from "react";

import { ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { colors } from "@/styles/design-tokens";

export type ChatComposerVariant = "compact" | "full";

export interface ChatComposerProps {
  /** Visual variant - "compact" for floating chat, "full" for full-page chat */
  variant?: ChatComposerVariant;
  /** Current input value */
  value: string;
  /** Called when input value changes */
  onChange: (value: string) => void;
  /** Called when user submits (Enter key or button click) */
  onSend: () => void;
  /** Whether the chat is currently streaming a response */
  isStreaming: boolean;
  /** Placeholder text (default: "Message...") */
  placeholder?: string;
  /** Additional className for the wrapper div */
  className?: string;
}

/**
 * Shared chat input composer with consistent behavior across chat surfaces.
 *
 * Key behaviors:
 * - Textarea is NEVER disabled (allows type-ahead while streaming)
 * - Only the send button is disabled during streaming or when empty
 * - Enter key sends message (Shift+Enter for newline)
 *
 * @example
 * ```tsx
 * // Compact variant (for floating ChatWindow)
 * <ChatComposer
 *   variant="compact"
 *   value={inputValue}
 *   onChange={setInputValue}
 *   onSend={handleSend}
 *   isStreaming={isStreaming}
 * />
 *
 * // Full variant (for /chats/[id] page)
 * <ChatComposer
 *   variant="full"
 *   value={inputValue}
 *   onChange={setInputValue}
 *   onSend={handleSend}
 *   isStreaming={isStreaming}
 * />
 * ```
 */
export const ChatComposer = forwardRef<HTMLTextAreaElement, ChatComposerProps>(
  function ChatComposer(
    {
      variant = "compact",
      value,
      onChange,
      onSend,
      isStreaming,
      placeholder = "Message...",
      className = "",
    },
    ref
  ) {
    const canSend = value.trim().length > 0 && !isStreaming;

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) {
          onSend();
        }
      }
    };

    const handleSendClick = () => {
      if (canSend) {
        onSend();
      }
    };

    // Variant-specific styles
    const isCompact = variant === "compact";

    const textareaClassName = isCompact
      ? "max-h-[50px] min-h-[36px] resize-none border-0 text-sm ring-1 ring-transparent transition-shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 sm:max-h-[80px]"
      : "min-h-[52px] resize-none rounded-[18px] border px-4 py-3 text-sm ring-1 ring-transparent transition-shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-0";

    const buttonClassName = isCompact
      ? "h-9 w-9 flex-shrink-0 rounded-full p-0"
      : "h-10 w-10 flex-shrink-0 rounded-full p-0";

    const textareaStyle = isCompact
      ? { backgroundColor: colors.accent.lightestGray }
      : {
          backgroundColor: colors.accent.lightestGray,
          borderColor: colors.divider.default,
        };

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Chat message input"
          className={textareaClassName}
          style={textareaStyle}
          // NOTE: Never disabled - allows type-ahead while streaming
        />
        <Button
          type="button"
          onClick={handleSendClick}
          disabled={!canSend}
          aria-label="Send message"
          className={buttonClassName}
          style={{
            backgroundColor: canSend ? colors.stroke.pureBlack : colors.accent.lightGray,
          }}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }
);
